import { useState } from 'react';
import { useForm, useSelector } from '@tanstack/react-form';
import { IonAlert, IonButton, IonInput, IonSpinner } from '@ionic/react';
import {
  FIELD_CLASS,
  SheetLayout,
  SheetModal,
  ThemeSwitcher,
} from '@mobile/ui';
import { logout, useSession } from '@shared/auth';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  FieldError,
  LABEL_CLASS,
  Section,
} from '@shared/ui';
import { cn, haptic } from '@shared/utils';
import type { DietSettings } from '@diet/types';

export interface SettingsSheetProps {
  /** 面板是否開啟。IonModal 為常駐元件，以此控制而非條件渲染 */
  isOpen: boolean;
  settings: DietSettings;
  /**
   * 儲存設定。**必須回傳 Promise**——面板要靠它判斷何時解除送出中狀態，
   * 型別若是 `void`，重複送出就擋不掉。
   */
  onSubmit: (settings: DietSettings) => Promise<void>;
  onClose: () => void;
}

const FORM_ID = 'settings-form';

/** 目標熱量的合理範圍，與後端的驗證一致。上下界都是在擋誤觸，不是營養學建議值。 */
const GOAL_MIN = 500;
const GOAL_MAX = 10000;

/**
 * 面板的兩個頁面。
 *
 * `preferences` 是使用者會反覆回來調的 app 設定，`system` 是帳號與登出這類一年動不到
 * 一次的系統功能。分頁而非分區，是因為兩者的**操作語意**不同：前者要填完再提交，
 * 後者是按下去就生效。混在同一頁時，底部的「取消／儲存」會連帶把登出也框進它的
 * 管轄範圍，讀起來像是「登出也要按儲存才算數」。
 */
type SettingsPage = 'preferences' | 'system';

export function SettingsSheet({
  isOpen,
  settings,
  onSubmit,
  onClose,
}: SettingsSheetProps) {
  return (
    <SheetModal
      isOpen={isOpen}
      onClose={onClose}
      /* 設定項少，面板比記錄表單矮。兩頁共用同一高度，換頁時面板不會抽動 */
      heightClass="[--height:60%]"
    >
      {/* 內容於開啟時才掛載，頁面與表單狀態每次開啟都是乾淨的，不需 key 或 reset */}
      <SettingsPanel
        settings={settings}
        onSubmit={onSubmit}
        onClose={onClose}
      />
    </SheetModal>
  );
}

type SettingsPanelProps = Omit<SettingsSheetProps, 'isOpen'>;

/**
 * 目標熱量的表單值。
 *
 * 存成字串而非 `number | null`：輸入途中會經過 `''` 與 `'18'` 這種還不是最終值的
 * 狀態，而「留空」本身也是一個有意義的答案（不設目標）。轉型是送出那一刻的事。
 */
interface SettingsFormValues {
  goal: string;
}

/**
 * 目標熱量的驗證。
 *
 * **留空是合法的**——清空欄位再儲存就是「取消目標」，故不做必填檢查。
 */
function validateGoal({ value }: { value: string }) {
  if (value.trim() === '') return undefined;

  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return '請輸入數字，例如 1800';
  if (parsed < GOAL_MIN || parsed > GOAL_MAX) {
    return `目標請介於 ${GOAL_MIN}–${GOAL_MAX} kcal`;
  }

  return undefined;
}

/**
 * 設定表單。
 *
 * 抽成具名 hook 有兩個作用：一是把「表單值 → `DietSettings`」的轉換收在一處，
 * 二是讓 `PreferencesPage` 能以 `ReturnType` 取得完整的表單型別——TanStack Form 的
 * 表單型別帶著十幾個驗證器泛型，手寫在 props 上不現實。
 */
function useSettingsForm(
  initialGoal: string,
  onSubmit: (settings: DietSettings) => Promise<void>,
) {
  const defaultValues: SettingsFormValues = { goal: initialGoal };

  return useForm({
    defaultValues,
    /* 一定要 await：`isSubmitting` 以這個 Promise 的完成為準（見 EntryFormSheet） */
    onSubmit: async ({ value }) => {
      const goal = value.goal.trim();

      await onSubmit({
        /* 留空即為不設目標：清除是「把欄位清掉再儲存」，不需要另一顆刪除鍵 */
        dailyCalorieGoal: goal === '' ? null : Math.round(Number(goal)),
      });
    },
    onSubmitInvalid: () => haptic('error'),
  });
}

type SettingsForm = ReturnType<typeof useSettingsForm>;

function SettingsPanel({ settings, onSubmit, onClose }: SettingsPanelProps) {
  const initialGoal =
    settings.dailyCalorieGoal === null ? '' : String(settings.dailyCalorieGoal);

  const [page, setPage] = useState<SettingsPage>('preferences');

  /*
   * 表單建在這一層而非 `PreferencesPage`：使用者改了數字、切去系統頁看一眼、再返回，
   * 輸入還在。表單若跟著頁面卸載，那趟來回就會把他打的字吃掉。
   */
  const form = useSettingsForm(initialGoal, onSubmit);

  const goal = useSelector(form.store, (state) => state.values.goal);
  const isSubmitting = useSelector(form.store, (state) => state.isSubmitting);

  /* 系統頁要據此決定登出前是否攔一下，見 SystemPage */
  const hasUnsavedChanges = goal.trim() !== initialGoal;

  const goToPage = (next: SettingsPage) => {
    haptic('tap');
    setPage(next);
  };

  if (page === 'system') {
    return (
      <SystemPage
        hasUnsavedChanges={hasUnsavedChanges}
        onBack={() => goToPage('preferences')}
      />
    );
  }

  return (
    <PreferencesPage
      form={form}
      isSubmitting={isSubmitting}
      onClose={onClose}
      onOpenSystem={() => goToPage('system')}
    />
  );
}

interface PreferencesPageProps {
  form: SettingsForm;
  /** 送出中：整組操作鍵停用，儲存鍵改顯示進度 */
  isSubmitting: boolean;
  onClose: () => void;
  onOpenSystem: () => void;
}

/**
 * 常用設定頁：使用者會反覆回來調的項目。
 *
 * 這一頁是純粹的表單——底部的「取消／儲存」只管得到上方的欄位，沒有第二種語意混進來。
 * 之後新增設定項是往下加一個分區，版面不必重排。
 */
function PreferencesPage({
  form,
  isSubmitting,
  onClose,
  onOpenSystem,
}: PreferencesPageProps) {
  return (
    <SheetLayout
      title="設定"
      /*
        系統設定入口放 header：放在內容底部會緊貼 footer，被讀成「取消／儲存」的一員。
        送出中停用，否則切走頁面會看不到儲存的成敗。
      */
      headerEnd={
        <IonButton
          onClick={onOpenSystem}
          disabled={isSubmitting}
          className="text-muted-foreground"
        >
          <span className="text-sm">系統</span>
          <ChevronRightIcon size={16} />
        </IonButton>
      }
      footer={
        <>
          <IonButton
            fill="outline"
            onClick={onClose}
            disabled={isSubmitting}
            className="flex-1"
          >
            取消
          </IonButton>

          {/*
            送出中保留文字而非只留轉圈：純圖示的按鈕沒有可讀名稱，螢幕閱讀器會念成
            「按鈕」。兩顆鍵都是 flex-1，換字不會讓寬度跳動。
          */}
          <IonButton
            type="submit"
            form={FORM_ID}
            disabled={isSubmitting}
            className="flex-1"
          >
            {isSubmitting ? (
              <>
                <IonSpinner name="crescent" className="h-4 w-4" />
                <span className="ml-2">儲存中…</span>
              </>
            ) : (
              '儲存'
            )}
          </IonButton>
        </>
      }
    >
      <form
        id={FORM_ID}
        /*
         * 送出中仍擋一次：表單能由鍵盤 Enter 送出，那條路徑繞過按鈕的 `disabled`。
         */
        onSubmit={(event) => {
          event.preventDefault();
          if (isSubmitting) return;
          void form.handleSubmit();
        }}
        className="flex flex-col gap-6"
        noValidate
      >
        <Section
          title="每日目標"
          hint="設定後，每日總覽會顯示已攝取與剩餘的熱量。留空即不設目標。"
        >
          {/*
            同一個驗證函式同時掛 onChange 與 onSubmit，理由同 EntryFormSheet：
            只掛前者，沒動過的欄位不會被驗；只掛後者，改對之後訊息不會消失。
          */}
          <form.Field
            name="goal"
            validators={{ onChange: validateGoal, onSubmit: validateGoal }}
          >
            {(field) => (
              /*
                標籤在欄位外面，理由同 EntryFormSheet：IonInput 的浮動標籤位移是為
                `fill` 模式校準的，iOS mode 沒有 fill 會與輸入值重疊。
              */
              <label className="flex flex-col gap-1.5">
                <span className={LABEL_CLASS}>目標熱量（kcal）</span>
                <IonInput
                  aria-label="目標熱量（kcal）"
                  value={field.state.value}
                  onIonInput={(event) =>
                    field.handleChange(event.detail.value ?? '')
                  }
                  onIonBlur={field.handleBlur}
                  type="number"
                  inputmode="numeric"
                  min={GOAL_MIN}
                  max={GOAL_MAX}
                  step="10"
                  placeholder="未設定"
                  /* 送出中鎖住欄位，送出的值與畫面上的值才不會對不起來 */
                  disabled={isSubmitting}
                  className={cn(FIELD_CLASS, 'font-mono tabular-nums')}
                />
                <FieldError message={field.state.meta.errors[0]} />
              </label>
            )}
          </form.Field>
        </Section>
      </form>
    </SheetLayout>
  );
}

interface SystemPageProps {
  /** 常用設定頁的目標熱量已被改動但還沒儲存 */
  hasUnsavedChanges: boolean;
  onBack: () => void;
}

/**
 * 系統設定頁：帳號與登出這類一年動不到一次、但按下去立刻生效的功能。
 *
 * 這一頁**沒有 footer**。裡面每個動作都是按下即生效，沒有東西需要提交，硬掛一條
 * 「取消／儲存」只會讓人以為登出還要再確認一次才算數。
 *
 * `useSession` 放在這裡而非外層的 `DietApp`：這一頁要切進來才會掛載，查詢因此只發生在
 * 使用者真的來看帳號的時候。掛在 app 外殼的話，每次開 app 都會多打一次 `/api/me`
 * ——`AuthGate` 開場已經查過一次了。
 */
function SystemPage({ hasUnsavedChanges, onBack }: SystemPageProps) {
  const { status, user } = useSession();
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  /**
   * 登出。
   *
   * `logout()` 是**整頁導向**（送出隱藏表單到 auth-service，見 session.ts），不會回傳
   * 也永遠不會 resolve——所以這裡沒有把 `isLoggingOut` 收回 false 的路徑，也不需要：
   * 狀態一直維持到瀏覽器離開本頁為止。送出表單到實際換頁之間有可感知的空檔，不鎖住
   * 就會被連點成多次送出。
   */
  const startLogout = () => {
    setIsLoggingOut(true);
    logout();
  };

  const handleLogout = () => {
    haptic('tap');

    /*
     * 只在常用設定頁有未儲存變更時才確認。刪除紀錄不確認就直接執行（見 EntryFormSheet），
     * 這裡多這一步不是因為登出比較危險——而是導向一走，使用者在上一頁打進去的目標熱量
     * 就沒了，那是他按登出時沒在想的另一件事。沒改過東西就別擋路。
     */
    if (hasUnsavedChanges) {
      setIsConfirmOpen(true);
      return;
    }

    startLogout();
  };

  return (
    <>
      <SheetLayout
        title="系統設定"
        /* 導向途中停用返回：此時離開已經來不及，讓畫面維持在「正在登出」 */
        headerStart={
          <IonButton
            onClick={onBack}
            disabled={isLoggingOut}
            className="text-muted-foreground"
          >
            <ChevronLeftIcon size={16} />
            <span className="text-sm">設定</span>
          </IonButton>
        }
      >
        {/*
          外觀放在帳號之前：它是這一頁裡唯一會被反覆調整的項目，帳號與登出一年動不到
          一次。把常用的擺前面，而不是照「重要性」排——登出最重，但沒人是為了登出而
          打開設定的。
        */}
        <Section title="外觀">
          <ThemeSwitcher />
        </Section>

        <Section title="帳號">
          {/*
            `min-h` 固定為兩行內容的高度：只顯示一行時高度不變，載入完成才不會推動下方的登出鍵。
          */}
          <div
            className={cn(
              'flex min-h-[2.375rem] flex-col justify-center gap-0.5',
              'rounded-lg border border-border bg-card px-4 py-3',
            )}
          >
            {status === 'loading' ? (
              /*
               * 骨架而非「載入中…」：這段通常一閃即過，文字反而讓人注意到閃動。
               * 兩條的高度對應顯示名稱與 email 的行高，與載入完成後等高。
               */
              <>
                <span className="h-5 w-32 animate-pulse rounded bg-muted" />
                <span className="h-4 w-40 animate-pulse rounded bg-muted" />
              </>
            ) : (
              <>
                <span className="truncate text-sm text-foreground">
                  {user?.displayName ?? user?.email ?? '未知的登入身分'}
                </span>
                {/* 顯示名稱可能與 email 相同，重複顯示只是雜訊 */}
                {user?.email && user.email !== user.displayName && (
                  <span className="truncate text-xs text-muted-foreground">
                    {user.email}
                  </span>
                )}
              </>
            )}
          </div>

          {/* 登出是本頁唯一的動作，故用滿版；danger 色標示破壞性 */}
          <IonButton
            expand="block"
            fill="outline"
            color="danger"
            onClick={handleLogout}
            disabled={isLoggingOut}
            className="mt-2"
          >
            {isLoggingOut ? (
              <>
                <IonSpinner name="crescent" className="h-4 w-4" />
                <span className="ml-2">登出中…</span>
              </>
            ) : (
              '登出'
            )}
          </IonButton>
        </Section>
      </SheetLayout>

      <IonAlert
        isOpen={isConfirmOpen}
        onDidDismiss={() => setIsConfirmOpen(false)}
        header="尚未儲存變更"
        message="登出會離開本站，剛才修改的目標熱量不會保留。"
        buttons={[
          { text: '返回設定', role: 'cancel' },
          { text: '仍要登出', role: 'confirm', handler: startLogout },
        ]}
      />
    </>
  );
}
